import { CommonModule } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { challenge as ChallengeGen } from '@valvur/shared-grpc';
import { TranslatePipe } from '@ngx-translate/core';
import {
  CardBadgeComponent,
  CardBadgeConfig,
  CardBadgeTagColor,
} from '../../../../shared/components/ui/card-badge/card-badge.component';
import { ParentLearnProgressComponent } from '../../../parent-learning/components/parent-learn-progress/parent-learn-progress.component';

/**
 * Unified interface for card data that works with both Task and Course
 */
interface CardData {
  id?: string;
  title?: string;
  name?: string;
  courseName?: string;
  description?: string;
  courseDescription?: string;
  difficulty?: string;
  thumbnailUrl?: string;
  imageUrl?: string; // Course image or task cover for card display
  quizes?: Array<{ description?: string }>;
  rating?: number;
  courseJoined?: boolean;
  completionPercent?: number;
  taskCount?: number;
  completedTaskCount?: number;
}

export type ParentCourseCardState = 'new' | 'in-progress' | 'completed';

export type CourseCardVariant = 'default' | 'parent';

@Component({
  standalone: true,
  selector: 'app-course-card',
  imports: [CommonModule, TranslatePipe, CardBadgeComponent, ParentLearnProgressComponent],
  templateUrl: './course-card.component.html',
  styleUrl: './course-card.component.scss',
})
export class CourseCardComponent {
  variant = input<CourseCardVariant>('default');
  // Accept either Task or Course
  course = input<ChallengeGen.Task | ChallengeGen.Course>();
  isDraft = input<boolean>(false);
  isUploading = input<boolean>(false);

  // Normalize the data to a unified structure
  cardData = computed<CardData | undefined>(() => {
    const item = this.course();
    if (!item) return undefined;

    // Check if it's a Course (has courseName property)
    const isCourse = 'courseName' in item;

    if (isCourse) {
      const course = item as ChallengeGen.Course & {
        courseJoined?: boolean;
        completionPercent?: number;
        taskCount?: number;
        completedTaskCount?: number;
      };
      return {
        id: course.id,
        title: course.courseName,
        courseName: course.courseName,
        description: course.courseDescription,
        courseDescription: course.courseDescription,
        difficulty: course.difficulty,
        rating: course.averageRating,
        imageUrl: course.courseImageUrl || course.courseBadgeIcon,
        courseJoined: course.courseJoined,
        completionPercent: course.completionPercent ?? 0,
        taskCount: course.taskCount,
        completedTaskCount: course.completedTaskCount,
      };
    } else {
      const task = item as ChallengeGen.Task;
      return {
        id: task.id,
        title: task.title || task.name,
        name: task.name,
        description: task.description,
        difficulty: task.difficulty,
        thumbnailUrl: task.thumbnailUrl,
        imageUrl: task.coverUrl || task.thumbnailUrl,
        quizes: task.quizes,
        rating: task.rating,
      };
    }
  });

  status = computed(() => {
    const item = this.course();
    if (!item) return undefined;
    const isCourse = 'courseName' in item;
    return isCourse ? item.status : 'DRAFT';
  });
  // Get display title
  displayTitle = computed(() => {
    const data = this.cardData();
    if (this.isDraft()) {
      return data?.title || data?.courseName || data?.name || 'Unnamed Course';
    }
    return data?.title || data?.courseName || data?.name || '';
  });

  // Get display description
  displayDescription = computed(() => {
    const data = this.cardData();
    if (!data) return '';

    // For tasks, prefer quizes[0]?.description, fallback to description
    if (data.quizes && data.quizes.length > 0 && data.quizes[0]?.description) {
      return data.quizes[0].description;
    }

    // Otherwise use description or courseDescription
    return data.description || data.courseDescription || '';
  });

  // Get difficulty for display
  displayDifficulty = computed(() => this.cardData()?.difficulty);

  /** Course/task image URL for card display. Course: courseImageUrl|courseBadgeIcon. Task: coverUrl|thumbnailUrl. */
  displayImageUrl = computed(() => this.cardData()?.imageUrl);

  /** Rating for display (Course: averageRating, Task: rating). Formatted to 1 decimal when present. */
  displayRating = computed(() => {
    const r = this.cardData()?.rating;
    if (r == null || r <= 0) return null;
    return Number(r.toFixed(1));
  });

  /** Progress for display when course is joined. 0–100. */
  displayProgress = computed(() => {
    const data = this.cardData();
    if (!data?.courseJoined) return null;
    return data.completionPercent ?? 0;
  });

  readonly displayTaskCount = computed(() => this.cardData()?.taskCount ?? 0);

  readonly displayCompletedTaskCount = computed(
    () => this.cardData()?.completedTaskCount ?? 0,
  );

  readonly displayPercent = computed(() => this.cardData()?.completionPercent ?? 0);

  readonly parentCardState = computed((): ParentCourseCardState => {
    const data = this.cardData();
    if (!data || this.isDraft()) {
      return 'new';
    }
    const total = data.taskCount ?? 0;
    const completed = data.completedTaskCount ?? 0;
    const percent = data.completionPercent ?? 0;
    if (percent >= 100 || (total > 0 && completed >= total)) {
      return 'completed';
    }
    if (
      data.courseJoined ||
      completed > 0 ||
      percent > 0
    ) {
      return 'in-progress';
    }
    return 'new';
  });

  readonly showParentProgress = computed(() => {
    if (this.variant() !== 'parent' || this.isDraft()) {
      return false;
    }
    const state = this.parentCardState();
    return this.displayTaskCount() > 0 && state !== 'new';
  });

  /** Parent grid: difficulty + rating only (progress uses segment bar). */
  readonly parentBadges = computed<CardBadgeConfig[]>(() => {
    const result: CardBadgeConfig[] = [];
    const difficulty = this.displayDifficulty();
    if (difficulty && !this.isDraft()) {
      result.push({
        value: this.getDifficultyText(difficulty),
        translateKey: true,
        variant: 'tag',
        tagColor: this.getDifficultyColor(difficulty) as CardBadgeTagColor,
      });
    }
    const rating = this.displayRating();
    if (rating != null) {
      result.push({
        value: rating.toString(),
        iconUrl: '/public/svgs/rating.svg',
      });
    }
    return result;
  });

  /** Badges to display: difficulty, rating, progress. Built from card data. */
  badges = computed<CardBadgeConfig[]>(() => {
    const result: CardBadgeConfig[] = [];

    const difficulty = this.displayDifficulty();
    if (difficulty && !this.isDraft()) {
      result.push({
        value: this.getDifficultyText(difficulty),
        translateKey: true,
        variant: 'tag',
        tagColor: this.getDifficultyColor(difficulty) as CardBadgeTagColor,
      });
    }

    if (this.isDraft()) return result;

    const rating = this.displayRating();
    if (rating != null) {
      result.push({
        value: rating.toString(),
        iconUrl: '/public/svgs/rating.svg',
      });
    }

    const progress = this.displayProgress();
    if (progress != null) {
      result.push({ value: `${progress}%`, iconUrl: '' });
    }

    return result;
  });

  // Check if it's a course and draft (for showing spinner)
  isCourseDraft = computed(() => {
    const item = this.course();
    if (!item) return false;
    const isCourse = 'courseName' in item;
    return isCourse && item.status === 'DRAFT';
  });

  /** Icon badge + face image: green | orange | purple. Task: EASY,MEDIUM,HARD. Course: BEGINNER,INTERMEDIATE,ADVANCED,EXPERT. */
  getIconColor(difficulty: string | undefined): string {
    if (!difficulty) return 'green';
    const upper = difficulty.toUpperCase();
    if (upper === 'EASY' || upper === 'BEGINNER') return 'green';
    if (upper === 'MEDIUM' || upper === 'INTERMEDIATE') return 'orange';
    if (upper === 'HARD' || upper === 'ADVANCED' || upper === 'EXPERT')
      return 'purple';
    return 'green';
  }

  getDifficultyText(difficulty: string | undefined): string {
    if (!difficulty) return '';
    return `app.components.courseCard.difficulty.${difficulty.toLowerCase()}`;
  }

  /** Tag badge: green | gold | purple. Uses gold for medium/intermediate (distinct from icon orange). */
  getDifficultyColor(difficulty: string | undefined): string {
    if (!difficulty) return 'green';
    const upper = difficulty.toUpperCase();
    if (upper === 'EASY' || upper === 'BEGINNER') return 'green';
    if (upper === 'MEDIUM' || upper === 'INTERMEDIATE') return 'gold';
    if (upper === 'HARD' || upper === 'ADVANCED' || upper === 'EXPERT')
      return 'purple';
    return 'green';
  }

  getFaceImage(difficulty: string | undefined): string {
    const color = this.getIconColor(difficulty);
    return `/public/svgs/face-${color}.svg`;
  }
}
